// Pass 2 — Shared ERF-logo back navigator.
//
// Replaces the generic chevron "Back" control across admin / role-portal
// surfaces. Phase 2–8 modules inherit this component via AdminAppShell + any
// placeholder/detail shells that currently host their own Back control.
//
// Behavior (locked contract, mirrored by `test_erf_back_logo_parity.py`):
//   • Click AND Enter AND Space all invoke the same back-nav handler.
//   • If in-site history exists (window.history.length > 1), call
//     window.history.back() — matches the public footer-wordmark pattern.
//   • Otherwise navigate to the up-one-level parent of the current pathname
//     (strip the last path segment, e.g. /admin/integrations → /admin,
//     /admin/users/123 → /admin/users). Collapse to `fallbackPath` when the
//     parent collapses to an empty / root string.
//
// A11y:
//   • role="button", tabIndex={0}, aria-label (default "Return to previous
//     page", caller may override), aria-pressed never used.
//   • Visible focus ring provided by the global `*:focus-visible` rule in
//     frontend/src/index.css (2px solid #B8892B outline + 2px offset) —
//     the same ring that locks the public header/footer wordmark pass.
//   • Touch target wrapper ≥44×44px via padding (min-h 44 min-w 44) —
//     the ERF logo image itself may be smaller.
//
// Logo asset: /brand/erf_logo_horizontal.jpg (dark tone, same authoritative
// asset as the public <Wordmark/>). Set `tone="light"` to use the reversed
// asset on dark backgrounds.
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";

const ASSET = {
  dark: "/brand/erf_logo_horizontal.jpg",
  light: "/brand/erf_logo_horizontal_reversed.jpg",
};

function computeParentPath(pathname) {
  if (!pathname || pathname === "/" ) return null;
  const trimmed = pathname.replace(/\/+$/, "");
  const idx = trimmed.lastIndexOf("/");
  if (idx <= 0) return null;
  return trimmed.slice(0, idx) || null;
}

export default function ErfBackLogo({
  fallbackPath = "/admin",
  ariaLabel = "Return to previous page",
  tone = "dark",
  className = "",
  testId = "erf-back-logo",
  logoHeight = 28,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = React.useCallback(() => {
    if (typeof window !== "undefined" && window.history && window.history.length > 1) {
      window.history.back();
      return;
    }
    const parent = computeParentPath(location.pathname);
    navigate(parent || fallbackPath);
  }, [navigate, location.pathname, fallbackPath]);

  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      handleBack();
    }
  };

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={handleBack}
      onKeyDown={handleKey}
      aria-label={ariaLabel}
      data-testid={testId}
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 44,
        minHeight: 44,
        padding: "8px 12px",
        cursor: "pointer",
        borderRadius: 6,
        userSelect: "none",
      }}
    >
      <img
        src={ASSET[tone] || ASSET.dark}
        alt=""
        aria-hidden="true"
        draggable={false}
        style={{ height: logoHeight, width: "auto", display: "block" }}
      />
    </span>
  );
}

// Named export convenience for the shared design-system import style.
export { ErfBackLogo };
